import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, ShieldCheck, EyeOff, Save, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import RichTextEditor from './RichTextEditor';

const API = `${API_BASE}/api`;

const StatusBadge = ({ outline }) => {
  if (outline.status === 'approved') return <Badge className="bg-green-500/15 text-green-400" data-testid="outline-status-badge">Approved · visible to learners</Badge>;
  if (outline.published) return <Badge className="bg-amber-500/15 text-amber-300" data-testid="outline-status-badge">Edits pending approval</Badge>;
  if (outline.draft) return <Badge className="bg-amber-500/15 text-amber-300" data-testid="outline-status-badge">Draft · hidden from learners</Badge>;
  return <Badge className="bg-zinc-700/40 text-zinc-300" data-testid="outline-status-badge">Not generated</Badge>;
};

const ModuleEditor = ({ mod, mi, onChange }) => (
  <div className="rounded-lg border border-zinc-800 p-4 space-y-3" data-testid={`outline-module-${mi}`}>
    <Input value={mod.title} onChange={(e) => onChange({ ...mod, title: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white font-semibold" data-testid={`outline-module-title-${mi}`} />
    <Textarea value={mod.summary} onChange={(e) => onChange({ ...mod, summary: e.target.value })} rows={2} className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid={`outline-module-summary-${mi}`} />
    <div className="space-y-2 pl-3 border-l border-zinc-800">
      {mod.weeks.map((w, wi) => (
        <div key={wi} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2">
          <Input value={w.title} onChange={(e) => onChange({ ...mod, weeks: mod.weeks.map((x, i) => (i === wi ? { ...x, title: e.target.value } : x)) })} className="bg-zinc-800/60 border-zinc-700 text-zinc-200 text-sm" data-testid={`outline-week-title-${mi}-${wi}`} />
          <Textarea value={w.summary} onChange={(e) => onChange({ ...mod, weeks: mod.weeks.map((x, i) => (i === wi ? { ...x, summary: e.target.value } : x)) })} rows={2} className="bg-zinc-800/60 border-zinc-700 text-zinc-300 text-sm" data-testid={`outline-week-summary-${mi}-${wi}`} />
        </div>
      ))}
    </div>
  </div>
);

export default function ProgrammeOutlineTab({ programmeId, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [outline, setOutline] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState('');

  const apply = (o) => {
    setOutline(o);
    setDraft(o.draft ? { ...o.draft, outcomesText: (o.draft.learning_outcomes || []).join('\n') } : null);
  };

  const load = useCallback(() => {
    axios.get(`${API}/programmes/${programmeId}/outline`, { headers }).then((r) => apply(r.data)).catch(() => toast.error('Failed to load course outline'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId, token]);

  useEffect(() => { load(); }, [load]);

  const call = async (key, method, path, body, ok) => {
    setBusy(key);
    try {
      const res = await axios[method](`${API}/programmes/${programmeId}/outline${path}`, body, { headers });
      apply(res.data);
      toast.success(ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || 'Course outline action failed');
    } finally {
      setBusy('');
    }
  };

  const payload = () => ({ overview_html: draft.overview_html, modules: draft.modules, learning_outcomes: draft.outcomesText.split('\n').map((s) => s.trim()).filter(Boolean) });
  const save = () => call('save', 'put', '', payload(), 'Draft saved — approve it to show learners');
  const approve = async () => {
    setBusy('approve');
    try {
      await axios.put(`${API}/programmes/${programmeId}/outline`, payload(), { headers });
    } catch (e) {
      setBusy('');
      return toast.error('Could not save before approving');
    }
    call('approve', 'post', '/approve', {}, 'Course outline approved — learners can now see it');
  };

  if (!outline) return <p className="text-zinc-500 text-sm py-8" data-testid="outline-loading">Loading course outline...</p>;

  return (
    <div className="space-y-6" data-testid="programme-outline-tab">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-3"><h3 className="text-white font-semibold">Learner Course Outline</h3><StatusBadge outline={outline} /></div>
            <p className="text-sm text-zinc-400">AI writes an overview from your modules, weeks and activities. Learners only see it once you approve it.</p>
            {outline.approved_at && <p className="text-xs text-zinc-500 flex items-center gap-1.5" data-testid="outline-approved-info"><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> Last approved by {outline.approved_by_name || 'admin'} on {new Date(outline.approved_at).toLocaleString()}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => call('gen', 'post', '/generate', {}, 'Outline drafted — review and approve it')} className="border-teal-600/50 text-teal-300" data-testid="outline-generate-btn">
              {busy === 'gen' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}{outline.draft ? 'Regenerate' : 'Generate outline'}
            </Button>
            {outline.published && (
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => call('unpub', 'post', '/unpublish', {}, 'Outline hidden from learners')} className="border-zinc-700 text-zinc-300" data-testid="outline-unpublish-btn">
                <EyeOff className="h-4 w-4 mr-2" /> Hide from learners
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {outline.stale && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="outline-stale-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> Activities, weeks or modules have changed since this outline was generated. Regenerate it to keep it accurate.
        </div>
      )}

      {busy === 'gen' && <p className="text-sm text-zinc-400" data-testid="outline-generating">Writing the outline from your activities — this can take up to a minute...</p>}

      {draft && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader><CardTitle className="text-white text-base">Review & edit</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-zinc-400">Programme overview</Label>
              <div className="mt-1"><RichTextEditor value={draft.overview_html} onChange={(html) => setDraft((d) => ({ ...d, overview_html: html }))} /></div>
            </div>
            <div>
              <Label className="text-zinc-400">Learning outcomes (one per line)</Label>
              <Textarea value={draft.outcomesText} onChange={(e) => setDraft({ ...draft, outcomesText: e.target.value })} rows={6} className="bg-zinc-800 border-zinc-700 text-zinc-200 mt-1" data-testid="outline-outcomes-input" />
            </div>
            <div className="space-y-3">
              <Label className="text-zinc-400">Modules & weeks</Label>
              {(draft.modules || []).map((m, mi) => (
                <ModuleEditor key={mi} mod={m} mi={mi} onChange={(next) => setDraft((d) => ({ ...d, modules: d.modules.map((x, i) => (i === mi ? next : x)) }))} />
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={!!busy} onClick={save} className="border-zinc-700 text-zinc-300" data-testid="outline-save-btn"><Save className="h-4 w-4 mr-2" /> Save draft</Button>
              <Button disabled={!!busy} onClick={approve} className="bg-green-600 hover:bg-green-700 text-white" data-testid="outline-approve-btn"><ShieldCheck className="h-4 w-4 mr-2" /> Approve & show learners</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
